import { zodResolver } from '@hookform/resolvers/zod';
import { parse, subYears } from 'date-fns';
import * as WebBrowser from 'expo-web-browser';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Keyboard,
  Platform,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import type { PhoneNumber } from 'react-phone-number-input';
import { parsePhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';

import { useCustomer } from '@/api/customers/use-customer';
import type { StartOtpResponse } from '@/api/customers/use-customer-start-otp';
import { useCustomerStartOtp } from '@/api/customers/use-customer-start-otp';
import { useUpdateCustomer } from '@/api/customers/use-update-customer';
import { InputPhone } from '@/components/input-phone';
import type { PhoneCountry } from '@/components/input-phone/types';
import { validatePhoneNumber } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import {
  Button,
  Checkbox,
  ControlledInput,
  showErrorMessage,
  Text,
} from '@/ui';
import { Loader } from '@/ui/loader';

export type SignupFormProps = {
  firstName: string;
  lastName: string;
  dob: string;
  phone: string;
  email: string;
};

const signupFormSchema = z.object({
  firstName: z.string().min(2, 'Minimum 2 characters'),
  lastName: z.string().min(2, 'Minimum 2 characters'),
  dob: z
    .string()
    .regex(
      /^\d{2}\/\d{2}\/\d{4}$/,
      'Date of birth must be in the format MM/DD/YYYY'
    ),
  email: z.string().email(),
});

export type SignupFormType = z.infer<typeof signupFormSchema>;

interface FinishSignupProps {
  onBack: () => void;
  onDone: (phone: string) => void;
}

export default function FinishSignup({ onBack, onDone }: FinishSignupProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState<PhoneCountry>({ callingCode: '1' });
  const [dateModalVisible, setDateModalVisible] = useState(false);
  const [transactionalOptIn, setTransactionalOptIn] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const { mutate: updateCustomer, isPending: isUpdatingCustomer } =
    useUpdateCustomer();

  const { mutate: startOtp, isPending: isStartingOtp } = useCustomerStartOtp();

  const { data: customer, isPending: isGettingCustomer } = useCustomer();

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Sign Up Finish',
    });
  }, []);

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    formState: { isValid },
    reset,
  } = useForm<SignupFormType>({
    defaultValues: {
      firstName: customer?.firstname ?? '',
      lastName: customer?.lastname ?? '',
      dob: customer?.dob ?? '',
      email: customer?.email ?? '',
    },
    resolver: zodResolver(signupFormSchema),
  });

  const parsedPhoneNumber = React.useMemo((): PhoneNumber | null => {
    if (!customer || !customer.phone) return null;

    const phoneNumber = parsePhoneNumber(customer.phone);

    return phoneNumber;
  }, [customer]);

  React.useEffect(() => {
    if (parsedPhoneNumber) {
      setPhone(parsedPhoneNumber?.nationalNumber ?? '');
      setCountry({ callingCode: parsedPhoneNumber?.countryCallingCode });
    }
  }, [parsedPhoneNumber]);

  useEffect(() => {
    if (customer) {
      reset();
      setValue('firstName', customer.firstname);
      setValue('lastName', customer.lastname);
      setValue('email', customer.email);
      setValue('dob', customer.dob);
    }
  }, [customer, reset]);

  const isValidPhoneNumber = React.useMemo(() => {
    if (!country || !phone) return false;

    return validatePhoneNumber(country, phone);
  }, [country, phone]);

  const openUrl = async (url: string) => {
    if (Platform.OS === 'web') {
      window.open(url, '_blank');
    } else {
      await WebBrowser.openBrowserAsync(url);
    }
  };

  const handleContinue = (data: SignupFormProps) => {
    mixpanelService.track('Button Clicked', {
      'Button Name': 'Sign Up Finish',
    });

    const dobDateObject = parse(data.dob, 'MM/dd/yyyy', new Date());

    updateCustomer(
      {
        firstName: data.firstName,
        lastName: data.lastName,
        dob: dobDateObject,
        email: data.email,
        smsOptIn: marketingOptIn,
      },
      {
        onSuccess: () => {
          const formattedPhone = `+${country?.callingCode}${phone}`;

          startOtp(
            {
              phone: formattedPhone,
            },
            {
              onSuccess: (_: StartOtpResponse) => {
                onDone(formattedPhone);
              },
              onError: (e) => {
                showErrorMessage(e.message);
              },
            }
          );
        },
        onError: (e) => {
          console.log('e', e);
          showErrorMessage(e.message);
        },
      }
    );
  };

  if (isUpdatingCustomer || isStartingOtp) {
    return <Loader />;
  }

  const renderBody = () => {
    return (
      <View style={{ flexGrow: 1 }}>
        <View style={styles.formContainer}>
          <View style={{ gap: 16, flexGrow: 1 }}>
            <Text type="title1">Finish your Eon account!</Text>

            <View style={styles.inputsContainer}>
              <ControlledInput
                control={control}
                name="firstName"
                label="First name"
                onFocus={() => {
                  setDateModalVisible(false);
                }}
                onBlur={() => {
                  trigger('firstName');
                }}
                onChangeText={(text) => {
                  trigger('firstName');
                  setValue('firstName', text);
                }}
              />

              <ControlledInput
                control={control}
                name="lastName"
                label="Last name"
                onFocus={() => {
                  setDateModalVisible(false);
                }}
                onBlur={() => {
                  trigger('lastName');
                }}
                onChangeText={(text) => {
                  trigger('lastName');
                  setValue('lastName', text);
                }}
              />

              <ControlledInput
                control={control}
                name="dob"
                label="Date of birth - mm/dd/yyyy"
                type="date"
                onFocus={() => {
                  setDateModalVisible(true);
                }}
                onBlur={() => {
                  setDateModalVisible(false);
                  trigger('dob');
                }}
                onChangeText={(text) => {
                  setValue('dob', text);
                  trigger('dob');
                }}
                maximumDate={subYears(new Date(), 21)}
                dateModalVisible={dateModalVisible}
              />

              <ControlledInput
                control={control}
                name="email"
                label="Email"
                spellCheck={false}
                onFocus={() => {
                  setDateModalVisible(false);
                }}
                onBlur={() => {
                  trigger('email');
                }}
                onChangeText={(text) => {
                  trigger('email');
                  setValue('email', text);
                }}
                autoComplete="email"
              />

              <InputPhone
                onChangeText={(text: string) => setPhone(text)}
                onCountryChange={(c: PhoneCountry) => {
                  setCountry(c);
                }}
                onFocus={() => {
                  setDateModalVisible(false);
                }}
                defaultCountryCode={parsedPhoneNumber?.country}
                phone={parsedPhoneNumber?.nationalNumber}
              />
            </View>

            <View style={{ gap: 24 }}>
              <View
                style={{
                  paddingTop: 0,
                  flexDirection: 'row',
                  gap: 8,
                }}
              >
                <Checkbox
                  active={transactionalOptIn}
                  onPress={() => {
                    setTransactionalOptIn(!transactionalOptIn);
                  }}
                />

                <View style={{ flexShrink: 1, gap: 4 }}>
                  <Text type="bodyRegular">
                    I agree to receive automated transactional texts (bookings,
                    pickup/return, account, support) from Eon at this number.
                  </Text>

                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Consent is not required to use Eon. Msg frequency varies,
                    msg & data rates may apply. Reply STOP to opt out, HELP for
                    help.
                  </Text>
                </View>
              </View>

              <View
                style={{
                  paddingTop: 0,
                  flexDirection: 'row',
                  gap: 8,
                }}
              >
                <Checkbox
                  active={marketingOptIn}
                  onPress={() => {
                    setMarketingOptIn(!marketingOptIn);
                  }}
                />

                <View style={{ flexShrink: 1, gap: 4 }}>
                  <Text type="bodyRegular">
                    I agree to receive recurring promotional and marketing texts
                    from Eon at this number.
                  </Text>

                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Consent is not a condition of purchase. Msg frequency
                    varies, msg & data rates may apply. Reply STOP to opt out,
                    HELP for help.
                  </Text>
                </View>
              </View>
            </View>

            <View>
              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                See our{' '}
                <Text
                  style={{
                    textDecorationLine: 'underline',
                    color: theme.colors.text.secondary,
                  }}
                  onPress={() =>
                    openUrl(
                      'https://www.eonrides.com/legal/terms-privacy-policy'
                    )
                  }
                >
                  Privacy Policy
                </Text>{' '}
                and{' '}
                <Text
                  style={{
                    textDecorationLine: 'underline',
                    color: theme.colors.text.secondary,
                  }}
                  onPress={() =>
                    openUrl('https://app.eonrides.com/rental-terms')
                  }
                >
                  Terms of Service.
                </Text>
              </Text>
            </View>
          </View>

          <View style={[styles.buttonContainer]}>
            <Button
              tx="signup.continue"
              type="primary"
              onPress={handleSubmit(handleContinue)}
              disabled={isValid === false || !isValidPhoneNumber}
              style={{ width: '100%' }}
            />
          </View>
        </View>
      </View>
    );
  };

  return (
    <>
      {Platform.OS !== 'web' ? (
        <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
          <View style={{ flexGrow: 1 }}>
            <View style={styles.container}>{renderBody()}</View>
          </View>
        </TouchableWithoutFeedback>
      ) : (
        <View style={{ flexGrow: 1, marginTop: 32 }}>
          <View style={styles.container}>{renderBody()}</View>
        </View>
      )}
    </>
  );
}

const stylesheet = createStyleSheet(() => ({
  container: {
    flexGrow: 1,
    paddingLeft: 24,
    paddingRight: 24,
    paddingBottom: 24,
    gap: 16,
  },
  formContainer: {
    flexGrow: 1,
    gap: 32,
    justifyContent: 'space-between',
  },
  inputsContainer: {
    gap: 16,
  },
  buttonContainer: {
    paddingTop: 16,
    gap: 16,
    alignItems: 'center',
  },
}));
